import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Send, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

export function EmailComposeDialog({ open, onClose, lead, leadIds, isBulk = false }) {
  const { language: lang } = useLanguage();
  const t = (fr, en) => lang === 'fr' ? fr : en;
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setTo(lead?.email || '');
      setSubject('');
      setBody('');
      setResult(null);
      loadTemplates();
    }
  }, [open, lead]);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get('/email/templates');
      setTemplates(data.templates);
    } catch { /* ignore */ }
  };

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject);
    setBody(tpl.body);
  };

  const handleSend = async () => {
    if (!subject || !body) {
      toast.error(t('Objet et corps requis', 'Subject and body required'));
      return;
    }
    setSending(true);
    try {
      if (isBulk && leadIds?.length > 0) {
        const { data } = await api.post('/email/send-bulk', { lead_ids: leadIds, subject, body });
        setResult(data);
        toast.success(t(`${data.sent} emails envoyes`, `${data.sent} emails sent`));
      } else {
        await api.post('/email/send', { to_email: to, subject, body, lead_id: lead?.lead_id });
        toast.success(t('Email envoye', 'Email sent'));
        onClose();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Erreur d\'envoi', 'Send error'));
    } finally { setSending(false); }
  };

  const variables = '{{prenom}}, {{nom}}, {{ville}}, {{service}}, {{nom_entreprise}}, {{telephone}}';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            {isBulk
              ? t(`Envoi groupe (${leadIds?.length || 0} prospects)`, `Bulk send (${leadIds?.length || 0} leads)`)
              : t('Envoyer un email', 'Send email')}
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="py-4 space-y-3 text-center">
            <p className="text-lg font-semibold">{t('Envoi termine', 'Sending complete')}</p>
            <div className="flex justify-center gap-6">
              <div><span className="text-2xl font-bold text-green-600">{result.sent}</span><br /><span className="text-xs text-muted-foreground">{t('Envoyes', 'Sent')}</span></div>
              <div><span className="text-2xl font-bold text-red-500">{result.errors}</span><br /><span className="text-xs text-muted-foreground">{t('Erreurs', 'Errors')}</span></div>
            </div>
            <Button onClick={onClose}>{t('Fermer', 'Close')}</Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="email-compose-form">
            {!isBulk && (
              <div className="space-y-1.5">
                <Label>{t('Destinataire', 'To')}</Label>
                <Input value={to} onChange={e => setTo(e.target.value)} placeholder="email@example.com" data-testid="email-to" />
              </div>
            )}

            {templates.length > 0 && (
              <div className="space-y-1.5">
                <Label>{t('Template', 'Template')}</Label>
                <Select onValueChange={v => { const tpl = templates.find(t => t.template_id === v); if (tpl) applyTemplate(tpl); }}>
                  <SelectTrigger data-testid="email-template-select"><SelectValue placeholder={t('Choisir un template...', 'Choose template...')} /></SelectTrigger>
                  <SelectContent>
                    {templates.map(tpl => <SelectItem key={tpl.template_id} value={tpl.template_id}>{tpl.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>{t('Objet', 'Subject')}</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder={t('Objet de l\'email', 'Email subject')} data-testid="email-subject" />
            </div>

            <div className="space-y-1.5">
              <Label>{t('Message', 'Message')}</Label>
              <Textarea value={body} onChange={e => setBody(e.target.value)} rows={8} placeholder={t('Corps de l\'email...', 'Email body...')} data-testid="email-body" />
              <p className="text-xs text-muted-foreground">{t('Variables disponibles', 'Available variables')}: {variables}</p>
            </div>

            {isBulk && (
              <p className="text-sm bg-amber-500/10 text-amber-700 p-3 rounded">
                {t(
                  `Vous allez envoyer ${leadIds?.length} emails. Les variables seront personnalisees pour chaque prospect.`,
                  `You will send ${leadIds?.length} emails. Variables will be personalized per lead.`
                )}
              </p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>{t('Annuler', 'Cancel')}</Button>
              <Button onClick={handleSend} disabled={sending || (!isBulk && !to)} data-testid="send-email-btn">
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                {sending ? t('Envoi...', 'Sending...') : t('Envoyer', 'Send')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
